import { useTranslation } from 'react-i18next';
import { useFeatureAgent, type Agent } from '@/entities/agent';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import { useCurrentAgent, useIsCurrentAgentMissing } from '@/features/chats/stores';

/**
 * The agent a message is attributed to: the one it names, else the chat's current agent.
 *
 * A message outlives its agent's card, so one whose card can no longer be loaded gets a stand-in
 * rather than staying `undefined`, which every consumer would otherwise read as still loading.
 */
export const useMessageAgent = (
  agentId: string | undefined,
): { agent: Agent | undefined; isLoading: boolean } => {
  const { t } = useTranslation('chats');
  const contextAgent = useCurrentAgent();
  const isContextAgentMissing = useIsCurrentAgentMissing();
  const workspaceId = useCurrentWorkspaceId();
  const {
    agent: featureAgent,
    isLoading,
    isMissing: isFeatureAgentMissing,
  } = useFeatureAgent(agentId, workspaceId);

  const loaded = agentId ? featureAgent : (contextAgent ?? undefined);
  const isMissing = agentId ? isFeatureAgentMissing : isContextAgentMissing;
  const agent =
    loaded ??
    (isMissing ? { id: agentId ?? '', title: t('chats.chatBubble.unavailableAgent') } : undefined);

  return { agent, isLoading };
};
